import { calculatePercentageChange, ROLE_BOM } from "@/lib/utils";
import { RootState } from "@/redux/store";
import {
  useGetCommunityNewsSettings,
  useGetDashboardCount,
  useGetNewsletter,
} from "@/services";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const useNewsLetterCard = () => {
  const [flashNews, setFlashNews] = useState([
    { text: "", expiry: "" },
    { text: "", expiry: "" },
  ]);

  const [selectedNewsletter, setSelectedNewsletter] = useState<string>("");

  const [files, setFiles] = useState([
    {
      title: "Newsletter 1",
      name: "",
      size: 3000,
      id: 1,
    },
    {
      title: "Newsletter 2",
      name: "",
      size: 3000,
      id: 2,
    },
  ]);

  const [cards, setCards] = useState([
    {
      id: "budget",
      title: "Budget",
      amount: { value: "", apiKey: "currentYearBudget" },
      prevData: { value: "", apiKey: "lastYearBudget", progress: "" },
      className: "bg-primary",
      approvalDate: {
        apiKey: "currentYearBudgetApprovalDate",
        value: "",
      },
      approvalCount: {
        apiKey: "currentYearBudgetApprovalCount",
        value: "",
      },
    },
    {
      id: "expense",
      title: "Actual Expense",
      amount: { value: "0", apiKey: "budgetExpense" },
      prevData: { value: "0", apiKey: "lastYearExpense", progress: "" },
      actual: { value: "0", apiKey: "actualExpense", progress: "" },
      className: "bg-red-100",
    },
    {
      id: "receivables",
      title: "Actual Receivables",
      amount: { value: "0", apiKey: "budgetIncome" },
      prevData: { value: "0", apiKey: "lastYearRecievables", progress: "" },
      actual: { value: "0", apiKey: "actualRecievables", progress: "" },
      className: "bg-green-100",
    },
  ]);

  const {
    communityData: communityDetail,
    currentFinanceYear,
    currentRole,
  } = useSelector((state: RootState) => state.currentData);

  const isBom = currentRole == ROLE_BOM;

  const {
    data: communityNewsSettings,
    isLoading: isLoadingNewsLoading,
    isError: isNewsSettingsError,
  } = useGetCommunityNewsSettings({
    community_id: communityDetail?.communityId || "",
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: newsLetter, isLoading: isNewsLetterLoading } = useGetNewsletter(
    {
      community_id: communityDetail?.communityId || "",
      newsletter_no: selectedNewsletter,
      queryParams: {
        enabled: !!(communityDetail?.communityId && selectedNewsletter),
      },
    }
  );

  useEffect(() => {
    const loadImage = async () => {
      if (!newsLetter) return;

      window.open(newsLetter?.data, "blank");

      setTimeout(() => {
        setSelectedNewsletter("");
      }, 100);
    };

    loadImage();
  }, [newsLetter]);

  const {
    data: prevYearsCounts,
    isLoading: isPrevYearsCountsLoading,
    refetch: refetchPrevYearsCount,
    isRefetching: isRefetchingPrevYearsCount,
  } = useGetDashboardCount({
    communityId: communityDetail?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityDetail?.communityId && currentFinanceYear),
    },
  });

  useEffect(() => {
    if (currentFinanceYear) {
      refetchPrevYearsCount();
    }
  }, [currentFinanceYear]);

  useEffect(() => {
    if (communityNewsSettings) {
      const _files = new Array(2)
        .fill(0)
        ?.map((file: any, index: number) => ({
          ...file,
          name:
            communityNewsSettings?.data?.[`newsletter${index + 1}FileName`] ||
            null,
          id: index,
        }))
        ?.filter((data: any) => data?.name);

      setFiles([..._files]);

      const _news = new Array(2)
        .fill(0)
        ?.map((news: any, index: number) => ({
          ...news,
          text: communityNewsSettings?.data?.[`flashNews${index + 1}`] || null,
          expiry: communityNewsSettings?.data?.[`flashNews${index + 1}Expiry`],
        }))
        ?.filter(
          (data: any) => data?.text && moment().isBefore(moment(data?.expiry))
        );

      setFlashNews([..._news]);
    } else if (isNewsSettingsError) {
      setFlashNews([]);
      setFiles([]);
    }
  }, [communityNewsSettings, isNewsSettingsError]);

  useEffect(() => {
    if (prevYearsCounts) {
      setCards((prevCards) =>
        prevCards.map((data: any) => {
          const amount = {
            ...data.amount,
            value: prevYearsCounts?.data?.[data?.amount?.apiKey],
          };

          const actual = {
            ...data.actual,
            value: prevYearsCounts?.data?.[data?.actual?.apiKey],
          };

          const _prevData = calculatePercentageChange(
            prevYearsCounts?.data?.[data?.amount?.apiKey],
            prevYearsCounts?.data?.[data?.prevData?.apiKey]
          );

          const prevData = {
            ...data.prevData,
            value: _prevData?.value,
            progress: _prevData?.progress,
          };

          return { ...data, amount, prevData, actual };
        })
      );
    }
  }, [prevYearsCounts]);

  const onNewsletterClick = (index: number) => {
    setSelectedNewsletter(String(index + 1));
  };

  return {
    files,
    cards,
    flashNews,
    prevYearsCounts,
    setSelectedNewsletter,
    onNewsletterClick,
    isPrevYearsCountsLoading,
    isLoadingNewsLoading,
    isNewsLetterLoading,
    isRefetchingPrevYearsCount,
    currentFinanceYear,
    isBom,
  };
};

export default useNewsLetterCard;
